'use client'

import { useCallback, useEffect } from 'react'
import { datadogLogs } from '@datadog/browser-logs'
import { useGetIdleState, useUIActions } from 'src/store/ui'
import { useGetPlayerChannel } from 'src/store/socket'
import { useGetCurrentPlayerId } from 'src/store/player'
import { IdleBanner } from './components/IdleBanner'
import { WarningBanner } from './components/WarningBanner'
import { isIdleBannerDisabled } from './utils/isIdleBannerDisabled'

type Props = Readonly<{
  gameId: string
}>

export function IdleBannerWrapper({ gameId }: Props) {
  const playerChannel = useGetPlayerChannel()
  const uiActions = useUIActions()
  const idleState = useGetIdleState()
  const currentPlayerId = useGetCurrentPlayerId()
  const isIdleDisabled = isIdleBannerDisabled()

  const handleReconnect = useCallback(() => {
    uiActions.resetIdleState()

    if (playerChannel) {
      playerChannel.push('client:stream:resumed', {})

      datadogLogs.logger.info('idle_time', {
        user_action: {
          type: 'stream_resumed',
          playerId: currentPlayerId,
          gameId
        }
      })
    }
  }, [currentPlayerId, gameId, playerChannel, uiActions])

  useEffect(() => {
    const handleActivity = () => {
      if (idleState === 'warning') {
        uiActions.resetIdleState()
      }
    }

    window.addEventListener('mousemove', handleActivity)
    window.addEventListener('keydown', handleActivity)
    window.addEventListener('scroll', handleActivity)

    return () => {
      window.removeEventListener('mousemove', handleActivity)
      window.removeEventListener('keydown', handleActivity)
      window.removeEventListener('scroll', handleActivity)
    }
  }, [idleState, playerChannel, uiActions])

  if (isIdleDisabled) return null
  if (idleState === 'paused') return <IdleBanner onReconnect={handleReconnect} />
  if (idleState === 'warning') return <WarningBanner />
  return null
}
